import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { appStylingApi } from '../services/api';
import { useTheme } from './ThemeContext';

const StylingContext = createContext({});

// In dark mode a very dark brand colour (e.g. pure black) is unreadable when
// used as accent/link text on dark backgrounds — and `--primary` drives both
// button backgrounds AND `text-primary` link/hover colour. Raise its lightness
// to a readable floor while preserving hue + saturation. Light mode is untouched.
const DARK_MIN_LIGHTNESS = 65;
function adjustHslForDark(hslString) {
  const m = /^\s*(\d+)\s+(\d+)%\s+(\d+)%\s*$/.exec(hslString);
  if (!m) return hslString;
  const h = Number(m[1]);
  const s = Number(m[2]);
  let l = Number(m[3]);
  if (l < DARK_MIN_LIGHTNESS) l = DARK_MIN_LIGHTNESS;
  return `${h} ${s}% ${l}%`;
}

function parseHsl(hslString) {
  const m = /^\s*(\d+)\s+(\d+)%\s+(\d+)%\s*$/.exec(hslString || '');
  if (!m) return null;
  return { h: Number(m[1]), s: Number(m[2]), l: Number(m[3]) };
}

// Convert hex to HSL for Tailwind CSS variable format
function hexToHSL(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const GOOGLE_FONT_MAP = {
  'Inter': 'Inter:wght@400;500;600;700',
  'Plus Jakarta Sans': 'Plus+Jakarta+Sans:wght@400;500;600;700',
  'DM Sans': 'DM+Sans:wght@400;500;600;700',
  'Manrope': 'Manrope:wght@400;500;600;700',
  'Outfit': 'Outfit:wght@400;500;600;700',
  'Geist': 'Geist:wght@400;500;600;700',
  'Sora': 'Sora:wght@400;500;600;700',
  'Rubik': 'Rubik:wght@400;500;600;700',
};

let loadedFont = null;

function loadGoogleFont(fontFamily) {
  if (loadedFont === fontFamily) return;
  const spec = GOOGLE_FONT_MAP[fontFamily];
  if (!spec) return;
  
  // Remove old link if any
  const oldLink = document.getElementById('app-custom-font');
  if (oldLink) oldLink.remove();

  const link = document.createElement('link');
  link.id = 'app-custom-font';
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${spec}&display=swap`;
  document.head.appendChild(link);
  loadedFont = fontFamily;
}

function applyStyles(styling, isDark) {
  const root = document.documentElement;

  if (styling.primaryColor) {
    const baseHsl = hexToHSL(styling.primaryColor);
    const parsed = parseHsl(baseHsl);
    // A near-black / neutral-dark brand primary (e.g. #000000) is unusable in
    // DARK mode — it floors to mid-grey, giving white text on grey (~2.4:1, WCAG
    // fail). In dark mode treat it as "unset" so the built-in indigo default (with
    // its designed foreground) stands. Light mode keeps the user's colour as-is.
    const unusable = isDark && parsed && parsed.s <= 12 && parsed.l <= 20;
    if (unusable) {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--primary-foreground');
      root.style.removeProperty('--ring');
    } else {
      const primary = isDark ? adjustHslForDark(baseHsl) : baseHsl;
      root.style.setProperty('--primary', primary);
      // Ring color for focus states
      root.style.setProperty('--ring', primary);
    }
  }

  if (styling.accentColor) {
    root.style.setProperty('--accent-custom', styling.accentColor);
  }

  if (styling.fontFamily) {
    root.style.setProperty('--font-sans', `"${styling.fontFamily}", ui-sans-serif, system-ui, sans-serif`);
    document.body.style.fontFamily = `"${styling.fontFamily}", ui-sans-serif, system-ui, sans-serif`;
    loadGoogleFont(styling.fontFamily);
  }

  // Set browser tab title from Login Screen Title
  if (styling.loginTitle) {
    document.title = styling.loginTitle;
  }

  applyFavicon(styling.faviconUrl);
}

// Apply a custom uploaded favicon. When cleared, remove the managed link so the
// built-in default favicons in index.html take over again.
function applyFavicon(url) {
  const id = 'app-custom-favicon';
  let link = document.getElementById(id);
  if (url) {
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    if (link.getAttribute('href') !== url) {
      link.setAttribute('href', url);
    }
  } else if (link) {
    link.remove();
  }
}

export function StylingProvider({ children }) {
  const { theme } = useTheme();
  const [styling, setStyling] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStyling = useCallback(async () => {
    try {
      const res = await appStylingApi.get();
      setStyling(res.data);
    } catch {
      // Use defaults silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStyling();
  }, [fetchStyling]);

  // Apply (and re-apply) styles whenever the loaded styling OR the theme
  // changes, so a dark-mode-adjusted primary colour follows theme toggles.
  useEffect(() => {
    if (styling) applyStyles(styling, theme === 'dark');
  }, [styling, theme]);

  const refresh = useCallback(async () => {
    const res = await appStylingApi.get();
    setStyling(res.data); // triggers the apply effect above
    return res.data;
  }, []);

  return (
    <StylingContext.Provider value={{ styling, loading, refresh }}>
      {children}
    </StylingContext.Provider>
  );
}

export function useStyling() {
  return useContext(StylingContext);
}
